/**
 * @group Functions
 * 
 * @summary
 * <blockquote>Analyzes an error object to determine its external error type and appropriate response details</blockquote>
 * 
 * @param {any} error - The error object to analyze
 * @param {NetworkErrorOptions} options - Configuration options for error handling
 * @returns {NetworkErrorResponse} Structured error response object
 * 
 * @example
 * ```typescript
 * try {
 *   await fetch('/api/data');
 * } catch (error) {
 *   const errorResponse = analyzeNetworkError(error, { operation: 'fetching user data' });
 *   console.log(errorResponse.type); // 'CONNECTION_ERROR'
 * }
 * ```
 */
import { NETWORK_ERROR_MESSAGES } from '@shared/constants';
import { getNetworkError } from './getNetworkError';
import { getStatusCodeForErrorType } from './getStatusCodeForErrorType';
import type { NetworkErrorOptions } from '../types/NetworkErrorOptions';
import type { NetworkErrorResponse } from '../types/NetworkErrorResponse';

export function analyzeNetworkError(
  error: any,
  options: NetworkErrorOptions = {}
): NetworkErrorResponse {
  const {
    includeTechnicalDetails = true,
    includeTimestamp = true,
    context = {},
    customMessage,
    operation: contextOperation
  } = options;

  let networkError = getNetworkError(error);
  networkError = {
    ...networkError,
    ...(includeTechnicalDetails && { technical: error?.message }),
    ...(includeTimestamp && { timestamp: new Date().toISOString() }),
    ...(Object.keys(context).length > 0 && { context }),
    details: `An error occurred during ${contextOperation}`,
    error: networkError.error || customMessage || NETWORK_ERROR_MESSAGES.UNKNOWN_ERROR
  };

  const statusCode = getStatusCodeForErrorType(networkError.type);
  networkError.statusCode = statusCode;

  return networkError;
} 